'use client';

import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	ActionIcon,
	Affix,
	CloseButton,
	Modal,
	NavLink,
	Pill,
	TextInput,
	Tooltip,
	VisuallyHidden,
} from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import {
	type ComponentType,
	createContext,
	type PropsWithChildren,
	type ReactNode,
	useContext,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	ArrestorDescription,
	CommunicationLinesDescription,
	CrossArmDescription,
	DripLoopDescription,
	MeterCabinetDescription,
	MeterDescription,
	PointOfAttachmentDescription,
	PoleGroundDescription,
	PowerDistributionPrimaryLinesDescription,
	PowerNeutralLineDescription,
	PrimaryFuseDescription,
	PrimaryInsulatorDescription,
	SecondaryLinesDescription,
	ServiceDropDescription,
	ServiceEntranceConductorDescription,
	ServiceMastDescription,
	ServiceMastGuyDescription,
	ServicePanelCircuitBreakerDescription,
	ServicePointDescription,
	TrainerDescription,
	TreeTrimServiceLineDescription,
	UtilityPoleDescription,
	UtilityTransformerDescription,
	WeatherheadDescription,
} from '../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/EquipmentDescriptions';
import {
	ArcingWireDescription,
	BrightToDimLightsDescription,
	BrokenGuyWireDescription,
	BrokenOrLeaningPoleDescription,
	ContactDefinition,
	DebrisLeftBehindByDTEDescription,
	DownWireDefinition,
	ElectricityAllOutDescription,
	ExposedUndergroundDescription,
	FlickeringLightsDescription,
	FrayingWireDescription,
	GLNDescription,
	InterruptibleServiceDescription,
	LowWireHazardDescription,
	MissingManholeDescription,
	ObjectOnWireDescription,
	PartialPowerDescription,
	PoleLeftInDitchOrYardDescription,
	PoleTransformerFireDescription,
	TrafficSignalOutDescription,
	TransformerPoleNoiseDescription,
	TrappedDefinition,
	TreeInContactWithWireDescription,
	TreesGrowingIntoWiresDescription,
	WireWithinReachDefinition,
} from '../../app/(client)/(authenticated)/reportProblem/questions/(premise)/problemType/ProblemDescriptions';
import { EquipmentResponsibilityDetails } from './EquipmentResponsibilityDetails';
import styles from './HelpModal.module.scss';
import { OverheadEquipmentDetails } from './overheadEquipment/OverheadEquipmentDetails';

interface HelpTopic {
	value: string;
	title: string;
	category: HelpTopicCategory;
	keywords: string[];
	content: ComponentType;
}

type RawHelpTopic = Omit<HelpTopic, 'value'>;

enum HelpTopicCategory {
	Reference = 'Reference',
	Equipment = 'Equipment',
	Problems = 'Problems',
	Other = 'Other',
}

function toTopicValue(title: string): string {
	return title
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '');
}

function toSearchWords(value: string): string[] {
	return value
		.toLowerCase()
		.split(/[^a-z0-9]+/)
		.filter(Boolean);
}

function removeTitleWordsFromKeywords(title: string, keywords: string[]): string[] {
	const titleWords = new Set(toSearchWords(title));

	return keywords
		.map((keyword) => keyword.trim().replace(/\s+/g, ' '))
		.filter((keyword) => {
			const keywordWords = toSearchWords(keyword);
			return keywordWords.length > 0 && !keywordWords.every((word) => titleWords.has(word));
		});
}

const helpTopicCategories: HelpTopicCategory[] = [
	HelpTopicCategory.Reference,
	HelpTopicCategory.Problems,
	HelpTopicCategory.Equipment,
	HelpTopicCategory.Other,
];

const rawHelpTopics: RawHelpTopic[] = [
	{
		title: 'Equipment responsibility',
		category: HelpTopicCategory.Reference,
		keywords: ['Customer owned equipment', 'DTE owned equipment'],
		content: EquipmentResponsibilityDetails,
	},
	{
		title: 'Overhead equipment',
		category: HelpTopicCategory.Reference,
		keywords: ['Distribution pole', 'Utility pole'],
		content: OverheadEquipmentDetails,
	},
	{
		title: 'Utility pole',
		category: HelpTopicCategory.Equipment,
		keywords: ['Power pole'],
		content: UtilityPoleDescription,
	},
	{
		title: 'Primary lines',
		category: HelpTopicCategory.Equipment,
		keywords: ['Power distribution lines'],
		content: PowerDistributionPrimaryLinesDescription,
	},
	{
		title: 'Transformer',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: UtilityTransformerDescription,
	},
	{
		title: 'Secondary lines',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: SecondaryLinesDescription,
	},
	{
		title: 'Cross arm',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: CrossArmDescription,
	},
	{
		title: 'Primary insulator',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: PrimaryInsulatorDescription,
	},
	{
		title: 'Arrestor',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: ArrestorDescription,
	},
	{
		title: 'Primary fuse',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: PrimaryFuseDescription,
	},
	{
		title: 'Power neutral line',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: PowerNeutralLineDescription,
	},
	{
		title: 'Pole ground',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: PoleGroundDescription,
	},
	{
		title: 'Service drop',
		category: HelpTopicCategory.Equipment,
		keywords: ['Service line'],
		content: ServiceDropDescription,
	},
	{
		title: 'Service point',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: ServicePointDescription,
	},
	{
		title: 'Trainer',
		category: HelpTopicCategory.Equipment,
		keywords: ['Cable trainer'],
		content: TrainerDescription,
	},
	{
		title: 'Meter',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: MeterDescription,
	},
	{
		title: 'Drip loop',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: DripLoopDescription,
	},
	{
		title: 'Weatherhead',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: WeatherheadDescription,
	},
	{
		title: 'Service entrance conductor',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: ServiceEntranceConductorDescription,
	},
	{
		title: 'Meter cabinet',
		category: HelpTopicCategory.Equipment,
		keywords: ['Meter socket', 'Meter jaws'],
		content: MeterCabinetDescription,
	},
	{
		title: 'Service panel',
		category: HelpTopicCategory.Equipment,
		keywords: ['Circuit breaker box', 'Fuse box'],
		content: ServicePanelCircuitBreakerDescription,
	},
	{
		title: 'Service mast',
		category: HelpTopicCategory.Equipment,
		keywords: ['Service riser'],
		content: ServiceMastDescription,
	},
	{
		title: 'Point of attachment',
		category: HelpTopicCategory.Equipment,
		keywords: [],
		content: PointOfAttachmentDescription,
	},
	{
		title: 'Communication lines',
		category: HelpTopicCategory.Equipment,
		keywords: ['Telecommunications lines', 'Cable lines', 'Phone lines', 'Internet lines'],
		content: CommunicationLinesDescription,
	},
	{
		title: 'Service mast guy',
		category: HelpTopicCategory.Equipment,
		keywords: ['Mast support wire'],
		content: ServiceMastGuyDescription,
	},
	{
		title: 'Tree trim (service line)',
		category: HelpTopicCategory.Other,
		keywords: ['Tree trimming around service line'],
		content: TreeTrimServiceLineDescription,
	},
	{
		title: 'Trapped',
		category: HelpTopicCategory.Problems,
		keywords: ['Public trapped'],
		content: TrappedDefinition,
	},
	{
		title: 'Contact with equipment',
		category: HelpTopicCategory.Problems,
		keywords: ['Public contact', 'Shock'],
		content: ContactDefinition,
	},
	{
		title: 'Downed wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Down wire', 'Wire down'],
		content: DownWireDefinition,
	},
	{
		title: 'Wire within reach',
		category: HelpTopicCategory.Problems,
		keywords: ['Low wire'],
		content: WireWithinReachDefinition,
	},
	{
		title: 'Exposed underground',
		category: HelpTopicCategory.Problems,
		keywords: ['Underground lines exposed'],
		content: ExposedUndergroundDescription,
	},
	{
		title: 'Manhole cover missing',
		category: HelpTopicCategory.Problems,
		keywords: ['Missing manhole'],
		content: MissingManholeDescription,
	},
	{
		title: 'Object on wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Branches on wire', 'Tree on wire', 'Balloons on wire'],
		content: ObjectOnWireDescription,
	},
	{
		title: 'Traffic signal out',
		category: HelpTopicCategory.Problems,
		keywords: ['Stoplight'],
		content: TrafficSignalOutDescription,
	},
	{
		title: 'Noise at pole',
		category: HelpTopicCategory.Problems,
		keywords: ['Hum', 'Loud noise', 'Transformer noise'],
		content: TransformerPoleNoiseDescription,
	},
	{
		title: 'Low wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Low hanging wire'],
		content: LowWireHazardDescription,
	},
	{
		title: 'Fraying wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Frayed wire', 'Exposed conductor'],
		content: FrayingWireDescription,
	},
	{
		title: 'Broken guy wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Guy wire', 'Support cable'],
		content: BrokenGuyWireDescription,
	},
	{
		title: 'Outage',
		category: HelpTopicCategory.Problems,
		keywords: ['Electricity all out', 'Power outage', 'Full outage'],
		content: ElectricityAllOutDescription,
	},
	{
		title: 'Partial power',
		category: HelpTopicCategory.Problems,
		keywords: ['One leg out'],
		content: PartialPowerDescription,
	},
	{
		title: 'Interruptible service',
		category: HelpTopicCategory.Problems,
		keywords: ['Interruptible air conditioning (IAC)', 'Cool Currents', 'Interruptible water heating (IWH)'],
		content: InterruptibleServiceDescription,
	},
	{
		title: 'Low / high voltage',
		category: HelpTopicCategory.Problems,
		keywords: ['Low voltage', 'High voltage', 'Bright to dim lights'],
		content: BrightToDimLightsDescription,
	},
	{
		title: 'Flickering lights',
		category: HelpTopicCategory.Problems,
		keywords: [],
		content: FlickeringLightsDescription,
	},
	{
		title: 'Broken or leaning pole',
		category: HelpTopicCategory.Problems,
		keywords: ['Damaged pole'],
		content: BrokenOrLeaningPoleDescription,
	},
	{
		title: 'Fire',
		category: HelpTopicCategory.Problems,
		keywords: ['Equipment fire', 'Pole fire', 'Transformer fire'],
		content: PoleTransformerFireDescription,
	},
	{
		title: 'Arcing wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Sparks', 'Flashes'],
		content: ArcingWireDescription,
	},
	{
		title: 'Tree in contact with wire',
		category: HelpTopicCategory.Problems,
		keywords: ['Tree touching wire'],
		content: TreeInContactWithWireDescription,
	},
	{
		title: 'Trees growing into wires',
		category: HelpTopicCategory.Problems,
		keywords: ['Branches near power lines'],
		content: TreesGrowingIntoWiresDescription,
	},
	{
		title: 'Debris left behind',
		category: HelpTopicCategory.Problems,
		keywords: ['Job site cleanup'],
		content: DebrisLeftBehindByDTEDescription,
	},
	{
		title: 'Pole left in ditch or yard',
		category: HelpTopicCategory.Problems,
		keywords: ['Utility pole left behind'],
		content: PoleLeftInDitchOrYardDescription,
	},

	{
		title: 'GLN',
		category: HelpTopicCategory.Other,
		keywords: ['Global location number'],
		content: GLNDescription,
	},
];

const helpTopics: HelpTopic[] = rawHelpTopics.map((topic) => ({
	...topic,
	keywords: removeTitleWordsFromKeywords(topic.title, topic.keywords),
	value: toTopicValue(topic.title),
}));

interface HelpTopicSelectionContextValue {
	selectedValue: string | undefined;
	onSelect: (value: string) => void;
	openedTooltipValue: string | null;
	onTooltipOpen: (value: string) => void;
	onTooltipClose: (value: string) => void;
}

interface HelpTopicSearchContextValue {
	searchTerm: string;
	onSearchTermChange: (nextSearchTerm: string) => void;
}

const HelpTopicSelectionContext = createContext<HelpTopicSelectionContextValue | null>(null);
const HelpTopicSearchContext = createContext<HelpTopicSearchContextValue | null>(null);

function useHelpTopicSelectionContext(): HelpTopicSelectionContextValue {
	const context = useContext(HelpTopicSelectionContext);

	if (!context) {
		throw new Error('useHelpTopicSelectionContext must be used within HelpTopicSelectionContext.Provider');
	}

	return context;
}

function useHelpTopicSearchContext(): HelpTopicSearchContextValue {
	const context = useContext(HelpTopicSearchContext);

	if (!context) {
		throw new Error('useHelpTopicSearchContext must be used within HelpTopicSearchContext.Provider');
	}

	return context;
}

function useFilteredTopics(topics: HelpTopic[], searchTerm: string): HelpTopic[] {
	const normalizedSearch = searchTerm.trim().toLowerCase();

	return useMemo(
		() =>
			topics.filter(({ title, keywords }) => {
				if (!normalizedSearch) {
					return true;
				}

				const searchTokens = toSearchWords(normalizedSearch);
				const words = [...toSearchWords(title), ...keywords.flatMap((keyword) => toSearchWords(keyword))];
				return searchTokens.every((token) => words.some((word) => word.startsWith(token)));
			}),
		[normalizedSearch, topics],
	);
}

interface HelpTopicNavLinkProps {
	topic: HelpTopic;
}

function HelpTopicNavLink({ topic }: HelpTopicNavLinkProps): ReactNode {
	const { selectedValue, onSelect, openedTooltipValue, onTooltipOpen, onTooltipClose } =
		useHelpTopicSelectionContext();
	const hasAlternateKeywords = topic.keywords.length > 0;
	const tooltipOpened = openedTooltipValue === topic.value;

	const navLink = (
		<NavLink
			component="button"
			type="button"
			label={topic.title}
			active={selectedValue === topic.value}
			onClick={() => onSelect(topic.value)}
			onFocus={() => onTooltipOpen(topic.value)}
			onBlur={() => onTooltipClose(topic.value)}
			onMouseEnter={() => onTooltipOpen(topic.value)}
			onMouseLeave={() => onTooltipClose(topic.value)}
		/>
	);

	if (!hasAlternateKeywords) {
		return navLink;
	}

	return (
		<Tooltip
			opened={tooltipOpened}
			label={
				<Pill.Group>
					{topic.keywords.map((keyword) => (
						<Pill key={keyword}>{keyword}</Pill>
					))}
				</Pill.Group>
			}
			position="right"
			withArrow={true}
			multiline={true}
			maw={320}
			zIndex={10001}
		>
			{navLink}
		</Tooltip>
	);
}

function HelpTopicSearch(): ReactNode {
	const { searchTerm, onSearchTermChange } = useHelpTopicSearchContext();
	const inputRef = useRef<HTMLInputElement>(null);
	const inputId = useId();

	// Give focus to the input when pressing F1
	useHotkeys(
		[['F1', () => inputRef.current?.focus(), { preventDefault: true }]],
		// Don't ignore on any elements so that double tapping doesn't trigger the browser help window
		[],
	);

	return (
		<>
			<VisuallyHidden>
				<label htmlFor={inputId}>Filter help topics</label>
			</VisuallyHidden>
			<TextInput
				ref={inputRef}
				data-autofocus={true}
				placeholder="Filter help topics"
				id={inputId}
				value={searchTerm}
				onChange={(event) => onSearchTermChange(event.currentTarget.value)}
				leftSection={<FontAwesomeIcon icon={faSearch} />}
				leftSectionPointerEvents="none"
				rightSection={
					<CloseButton
						aria-label="Clear filter"
						onClick={() => onSearchTermChange('')}
						style={{ display: searchTerm ? undefined : 'none' }}
					/>
				}
				rightSectionPointerEvents="all"
				className={styles['helpModalSearch']}
			/>
		</>
	);
}

function HelpModalContent(): ReactNode {
	const defaultTopic = helpTopics[0]?.value;
	const [activeTopic, setActiveTopic] = useState(defaultTopic);
	const [openedTooltipValue, setOpenedTooltipValue] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const filteredTopics = useFilteredTopics(helpTopics, searchTerm);
	const searchContextValue: HelpTopicSearchContextValue = {
		searchTerm,
		onSearchTermChange: setSearchTerm,
	};
	const effectiveActiveTopic = filteredTopics.some(({ value }) => value === activeTopic)
		? activeTopic
		: (filteredTopics[0]?.value ?? activeTopic);
	const activeTopicDetails = helpTopics.find(({ value }) => value === effectiveActiveTopic);
	const ActiveDescription = activeTopicDetails?.content;
	const filteredTopicGroups = helpTopicCategories
		.map((category) => ({
			category,
			topics: filteredTopics
				.filter((topic) => topic.category === category)
				.sort((a, b) => a.title.localeCompare(b.title)),
		}))
		.filter(({ topics }) => topics.length > 0);

	return (
		<div className={styles['helpModalContent']}>
			<HelpTopicSearchContext.Provider value={searchContextValue}>
				<HelpTopicSelectionContext.Provider
					value={{
						selectedValue: effectiveActiveTopic,
						onSelect: setActiveTopic,
						openedTooltipValue,
						onTooltipOpen: (value) => setOpenedTooltipValue(value),
						onTooltipClose: (value) => {
							setOpenedTooltipValue((currentValue) => (currentValue === value ? null : currentValue));
						},
					}}
				>
					<div className={styles['helpModalNav']}>
						<HelpTopicSearch />

						{filteredTopicGroups.map(({ category, topics }) => (
							<section key={category} className={styles['helpModalCategoryGroup']}>
								<h5 className={styles['helpModalCategoryTitle']}>{category}</h5>
								{topics.map((topic) => (
									<HelpTopicNavLink key={topic.value} topic={topic} />
								))}
							</section>
						))}

						{filteredTopics.length === 0 ? (
							<p className={styles['helpModalNoResults']}>No topics match the filter</p>
						) : null}
					</div>
				</HelpTopicSelectionContext.Provider>
			</HelpTopicSearchContext.Provider>

			<div className={styles['helpModalPanel']}>{ActiveDescription ? <ActiveDescription /> : null}</div>
		</div>
	);
}

interface TooltipLabelProps {
	tooltipId: string;
}

function TooltipLabel({ tooltipId }: TooltipLabelProps): ReactNode {
	return (
		<span id={tooltipId}>
			<b>Help</b>
			<br />
			<span className={styles['helpTooltipHint']}>
				Click or press <b>F1</b> to open
			</span>
		</span>
	);
}

interface HelpButtonTooltipProps extends PropsWithChildren {
	tooltipId: string;
}

function HelpButtonTooltip({ children, tooltipId }: HelpButtonTooltipProps): ReactNode {
	return (
		<Tooltip label={<TooltipLabel tooltipId={tooltipId} />} position="left" withArrow={true} zIndex={10001}>
			{children}
		</Tooltip>
	);
}

export function HelpModal(): ReactNode {
	const [opened, { open, close, toggle }] = useDisclosure(false);
	const tooltipId = useId();

	// Open when pressing F1
	useHotkeys(
		[['F1', open, { preventDefault: true }]],
		// Don't ignore on any elements so that double tapping doesn't trigger the browser help window
		[],
	);

	return (
		<HelpButtonTooltip tooltipId={tooltipId}>
			<Affix position={{ bottom: '50vh', right: '0.25rem' }} zIndex={999}>
				<ActionIcon
					size={'2rem'}
					color="grey"
					variant="outline"
					className={styles['helpButton']}
					onClick={toggle}
					aria-describedby={tooltipId}
				>
					<FontAwesomeIcon icon={faQuestionCircle} />
				</ActionIcon>

				<Modal
					opened={opened}
					onClose={close}
					centered={true}
					title="Help"
					zIndex={10000}
					className={styles['helpModal']}
				>
					<HelpModalContent />
				</Modal>
			</Affix>
		</HelpButtonTooltip>
	);
}
